import { useState, useCallback, useMemo } from "react";
import { usePagesApi } from "./Pages.api";
import { useQuery } from "react-query";
import dayjs from "dayjs";
import { Flex, Spinner, Text, Badge } from "@radix-ui/themes";
import { ChevronDown, ChevronRight, ArrowRight, ArrowLeft } from "lucide-react";
import { Browser } from "@/v2/assets/icons/Browser";
import {
  DateRangePicker,
  DateRange,
} from "@/components/shared/date-range-picker/DateRangePicker";
import { PageLayoutV2 } from "@/v2/components/layout/page-layout/PageLayout";
import { PageHeader } from "@/v2/components/layout";
import { useNavigate } from "react-router-dom";
import { PageColors, ENavigate } from "@/components/sidebar/sidebar.types";
import Switch from "@/components/shared/inputs/switch/Switch";
import styles from "./Pages.module.scss";

const Pages = () => {
  const pagesApi = usePagesApi();
  const navigate = useNavigate();

  const [dateRange, setDateRange] = useState<DateRange>(() => ({
    dateFrom: dayjs().startOf("day").subtract(7, "day").toISOString(),
    dateTo: dayjs().toISOString(),
  }));

  const [sortByQuackchatInit, setSortByQuackchatInit] = useState(false);

  const handleDateRangeChange = useCallback((newDateRange: DateRange) => {
    setDateRange({
      dateFrom: newDateRange.dateFrom,
      dateTo: dayjs().toISOString(),
    });
  }, []);

  const { data, isLoading } = useQuery({
    queryKey: ["pages", dateRange.dateFrom, dateRange.dateTo],
    queryFn: () =>
      pagesApi.getPagesStats({
        startDate: dateRange.dateFrom,
        endDate: dateRange.dateTo,
      }),
    refetchOnWindowFocus: false,
  });

  const numberOfQuackchatInit = useMemo(() => {
    return data?.reduce((acc, curr) => acc + curr.quackchatInitCount, 0) || 0;
  }, [data]);

  const [expandedPageId, setExpandedPageId] = useState<string | null>(null);

  const sortedPages = useMemo(() => {
    if (!data) return [];

    const pages = [...data];

    if (sortByQuackchatInit) {
      return pages.sort((a, b) => b.quackchatInitCount - a.quackchatInitCount);
    }

    // Default: sort by journeyCount (navigations)
    return pages.sort((a, b) => b.journeyCount - a.journeyCount);
  }, [data, sortByQuackchatInit]);

  const handlePageClick = useCallback((pageId: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setExpandedPageId((prev) => (prev === pageId ? null : pageId));
  }, []);

  const handleNavigateToPage = useCallback(
    (pageId: string) => {
      navigate(`/pages/${pageId}`);
    },
    [navigate]
  );

  const handlePageItemClick = useCallback(
    (pageId: string, e: React.MouseEvent) => {
      e.stopPropagation();
      handleNavigateToPage(pageId);
    },
    [handleNavigateToPage]
  );

  return (
    <PageLayoutV2>
      <Flex direction="column">
        <PageHeader
          title="Pages"
          description="View and manage pages with their associated guides and configurations."
          icon={
            <Browser size={24} fill={PageColors[ENavigate.PAGES] || "gray"} />
          }
          actions={
            <Flex gap="4" align="center">
              <Flex gap="2" align="center">
                <Text size="2" color="gray">
                  {sortByQuackchatInit
                    ? "Sort by Quackchat Init"
                    : "Sort by Navigations"}
                </Text>
                <Switch
                  value={sortByQuackchatInit}
                  onChange={setSortByQuackchatInit}
                />
              </Flex>
              <DateRangePicker
                value={dateRange}
                onChange={handleDateRangeChange}
              />
            </Flex>
          }
        />
      </Flex>

      {numberOfQuackchatInit > 0 && (
        <Flex px="6" direction="column" pt="4">
          <Text size="2" color="gray" highContrast>
            Total Quackchat Inits: {numberOfQuackchatInit}
          </Text>
        </Flex>
      )}

      <Flex px="6" direction="column" pt="4">
        {isLoading ? (
          <Flex justify="center" align="center" py="9">
            <Spinner size="3" />
          </Flex>
        ) : !data || data.length === 0 ? (
          <Flex justify="center" align="center" py="9">
            <Text size="2" color="gray" highContrast>
              No pages available
            </Text>
          </Flex>
        ) : (
          <div className={styles.container}>
            <div className={styles.list}>
              {sortedPages.map((page) => {
                const isExpanded = expandedPageId === page.id;
                return (
                  <div key={page.id} className={styles.listItem}>
                    <Flex direction="column" gap="3">
                      <Flex
                        justify="between"
                        align="center"
                        style={{ width: "100%" }}
                        onClick={(e) => handlePageItemClick(page.id, e)}
                      >
                        <Flex align="center" gap="3" style={{ flex: 1 }}>
                          <Flex
                            align="center"
                            gap="2"
                            onClick={(e) => {
                              e.stopPropagation();
                              handlePageClick(page.id, e);
                            }}
                          >
                            {isExpanded ? (
                              <ChevronDown
                                size={16}
                                className={styles.chevron}
                              />
                            ) : (
                              <ChevronRight
                                size={16}
                                className={styles.chevron}
                              />
                            )}
                          </Flex>
                          <Flex direction="column" gap="1" style={{ flex: 1 }}>
                            <Flex align="center" gap="2">
                              <Text size="3" weight="medium" highContrast>
                                {page.name}
                              </Text>
                              {page.isNew && (
                                <Badge size="1" color="orange" variant="soft">
                                  New
                                </Badge>
                              )}
                            </Flex>
                            {page.pageGuide && (
                              <Text size="2" color="gray">
                                Has guide configured
                              </Text>
                            )}
                          </Flex>
                        </Flex>
                        <Flex direction="column" gap="2">
                          <Badge size="2" color="blue" variant="soft">
                            {page.journeyCount === 0
                              ? "No"
                              : page.journeyCount + ""}{" "}
                            {page.journeyCount === 1
                              ? "Navigation"
                              : "Navigations"}
                          </Badge>
                          <Badge size="2" color="yellow" variant="soft">
                            {page.quackchatInitCount === 0
                              ? "No"
                              : page.quackchatInitCount + ""}{" "}
                            {page.quackchatInitCount === 1
                              ? "Quackchat Init"
                              : "Quackchat Inits"}
                          </Badge>
                        </Flex>
                      </Flex>
                      {isExpanded && (
                        <div className={styles.expandedContent}>
                          <Flex direction="column" gap="3">
                            {page.mostGoToPage && (
                              <div className={styles.pageInfo}>
                                <Flex align="center" gap="2" mb="2">
                                  <ArrowRight
                                    size={14}
                                    className={styles.icon}
                                  />
                                  <Text size="2" weight="medium" color="gray">
                                    Most Go To Page
                                  </Text>
                                </Flex>
                                <div
                                  className={styles.pageName}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigateToPage(page.mostGoToPage!.id);
                                  }}
                                >
                                  <Text size="2" weight="medium" highContrast>
                                    {page.mostGoToPage.name}
                                  </Text>
                                </div>
                              </div>
                            )}
                            {page.mostComingFromPage && (
                              <div className={styles.pageInfo}>
                                <Flex align="center" gap="2" mb="2">
                                  <ArrowLeft
                                    size={14}
                                    className={styles.icon}
                                  />
                                  <Text size="2" weight="medium" color="gray">
                                    Most Coming From Page
                                  </Text>
                                </Flex>
                                <div
                                  className={styles.pageName}
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleNavigateToPage(
                                      page.mostComingFromPage!.id
                                    );
                                  }}
                                >
                                  <Text size="2" weight="medium" highContrast>
                                    {page.mostComingFromPage.name}
                                  </Text>
                                </div>
                              </div>
                            )}
                            {!page.mostGoToPage && !page.mostComingFromPage && (
                              <Text size="2" color="gray">
                                No navigation data available
                              </Text>
                            )}
                          </Flex>
                        </div>
                      )}
                    </Flex>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </Flex>
    </PageLayoutV2>
  );
};

export default Pages;
